import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  Button,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Dialog,
  DialogContent,
} from "@mui/material";
import EditDialog from "../../Components/Common/EditDialog";
import { post, get } from "../../utils/api";
import SelectField from "../../Components/Masters/SelectField";
import { quarterlist } from "../../utils/constant";
import { textError } from "../../utils/validationMessage";

const AddGovernance = ({ addData, setsnackOpen, handleClose }) => {
  const [errors, setErrors] = useState({});
  const [hasPreviousEntries, setHasPreviousEntries] = useState(false);
  const [entryType, setEntryType] = useState("quarterly"); // "annual" or "quarterly"

  const initialState = {
    quarter: "",
    year: "",
  };
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;
  // Use completed-quarter logic: Q1 becomes available only after Mar ends, etc.
  const lastCompletedQuarter =
    currentMonth <= 3 ? 0 : currentMonth <= 6 ? 1 : currentMonth <= 9 ? 2 : 3;
  const startYear = 2021;
  const years = Array.from(
    { length: currentYear - startYear + 1 },
    (_, i) => startYear + i
  );

  const [formData, setFormData] = useState(initialState);
  const [formData2, setFormData2] = useState(initialState);
  const [editDialog, setEditDialog] = useState(false);
  const [backendQuarterlyYears, setBackendQuarterlyYears] = useState([]);
  const [backendAnnualYears, setBackendAnnualYears] = useState([]);
  const [backendQuarterIdsByYear, setBackendQuarterIdsByYear] = useState({});

  const filteredYears = useMemo(
    () => {
      const baseYears = years.filter((y) => Number(y) <= currentYear);
      // Annual should always show full year list.
      if (entryType !== "quarterly") return baseYears;

      // For quarterly, always keep full base year list visible (incl. 2025).
      // Backend years are used for validation/duplicates, not for hiding years.
      const sourceYears = baseYears;

      const selectedQuarter = Number(formData.quarter);
      if (!selectedQuarter) return sourceYears;

      // If user selected a future quarter (e.g. Q4 while current quarter is Q1),
      // current year becomes invalid, so hide it.
      if (selectedQuarter > lastCompletedQuarter) {
        return sourceYears.filter((y) => Number(y) < currentYear);
      }
      return sourceYears;
    },
    [
      years,
      currentYear,
      entryType,
      formData.quarter,
      lastCompletedQuarter,
      backendQuarterlyYears,
    ]
  );
  const filteredQuarterOptions = useMemo(() => {
    const selectedYear = Number(formData.year);
    // Before selecting year, show all quarter options.
    if (!selectedYear) return quarterlist;
    // For previous years, always allow all quarters.
    if (selectedYear < currentYear) return quarterlist;
    if (selectedYear > currentYear) return [];
    // For current year, allow only completed quarters.
    return quarterlist.filter((q) => Number(q.id) <= lastCompletedQuarter);
  }, [formData.year, currentYear, lastCompletedQuarter]);

  useEffect(() => {
    // If user picks current year and already selected a future quarter, clear it.
    if (entryType !== "quarterly") return;
    const selectedYear = Number(formData.year);
    const selectedQuarter = Number(formData.quarter);
    if (!selectedYear || !selectedQuarter) return;
    if (selectedYear === currentYear && selectedQuarter > lastCompletedQuarter) {
      setFormData((prev) => ({ ...prev, quarter: "" }));
    }
  }, [entryType, formData.year, formData.quarter, currentYear, lastCompletedQuarter]);

  useEffect(() => {
    // If quarter is future, clear current year selection (since it is hidden/invalid).
    if (entryType !== "quarterly") return;
    const selectedQuarter = Number(formData.quarter);
    if (!selectedQuarter) return;
    if (selectedQuarter > lastCompletedQuarter && Number(formData.year) === currentYear) {
      setFormData((prev) => ({ ...prev, year: "" }));
    }
  }, [entryType, formData.quarter, formData.year, lastCompletedQuarter, currentYear]);

  const [copyPrevious, setCopyPrevious] = useState("no");
  const [periodOptions, setPeriodOptions] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState("");
  const [copyYear, setCopyYear] = useState("");
  const [copyQuarter, setCopyQuarter] = useState("");
  const [showFormFields, setShowFormFields] = useState(false);

  // Track existing records to prevent duplicate quarter/year or year
  const [existingQuarterlyCombos, setExistingQuarterlyCombos] = useState(
    new Set()
  );
  const [existingAnnualYears, setExistingAnnualYears] = useState(new Set());
  const [existingQuarterlyYears, setExistingQuarterlyYears] = useState(
    new Set()
  );

  // Check if previous entries exist when modal opens
  useEffect(() => {
    if (addData) {
      // Check if any completed entries exist (for copy previous visibility)
      get("/governance/getGovernance?type=Completed&limit=1")
        .then((res) => {
          setHasPreviousEntries((res.data.data || []).length > 0);
        })
        .catch(() => setHasPreviousEntries(false));

      // Load all existing entries (Pending + Completed) to prevent duplicates
      const fetchAllEntries = async () => {
        try {
          const [pendingRes, completedRes] = await Promise.all([
            get("/governance/getGovernance?type=Pending"),
            get("/governance/getGovernance?type=Completed"),
          ]);

          const allEntries = [
            ...(pendingRes?.data?.data || []),
            ...(completedRes?.data?.data || []),
          ];

          const quarterToId = (value) => {
            const raw = String(value ?? "").trim().toLowerCase();
            const asNum = Number(raw);
            if (asNum >= 1 && asNum <= 4) return asNum;
            if (raw === "q1" || raw.includes("jan-mar")) return 1;
            if (raw === "q2" || raw.includes("apr-jun")) return 2;
            if (raw === "q3" || raw.includes("jul-sep")) return 3;
            if (raw === "q4" || raw.includes("oct-dec")) return 4;
            return null;
          };

          const quarterlySet = new Set();
          const quarterlyYearSet = new Set();
          const annualSet = new Set();
          const backendQuarterlyYearSet = new Set();
          const backendAnnualYearSet = new Set();
          const backendQuarterMap = {};

          allEntries.forEach((entry) => {
            const ref = entry?._governanceDataRef || {};
            const yearVal = Number(
              ref?.yearToSubmit ?? entry?.yearToSubmit ?? entry?.year
            );
            const quarterVal =
              ref?.quarterToSubmit ??
              entry?.quarterToSubmit ??
              entry?.quarter ??
              "";
            const typeVal = String(
              ref?.module ||
                entry?.entryType ||
                entry?.module ||
                (quarterVal ? "quarterly" : "annual")
            ).toLowerCase();

            if (typeVal === "quarterly" && yearVal && quarterVal) {
              quarterlySet.add(`${quarterVal}-${yearVal}`);
              quarterlyYearSet.add(String(yearVal));
              backendQuarterlyYearSet.add(Number(yearVal));
              const qId = quarterToId(quarterVal);
              if (qId) {
                const yearKey = String(yearVal);
                if (!backendQuarterMap[yearKey]) backendQuarterMap[yearKey] = new Set();
                backendQuarterMap[yearKey].add(Number(qId));
              }
            } else if (typeVal === "annual" && yearVal) {
              annualSet.add(String(yearVal));
              backendAnnualYearSet.add(Number(yearVal));
            }
          });

          setExistingQuarterlyCombos(quarterlySet);
          setExistingAnnualYears(annualSet);
          setExistingQuarterlyYears(quarterlyYearSet);
          setBackendQuarterlyYears(
            Array.from(backendQuarterlyYearSet).sort((a, b) => b - a)
          );
          setBackendAnnualYears(
            Array.from(backendAnnualYearSet).sort((a, b) => b - a)
          );
          setBackendQuarterIdsByYear(
            Object.fromEntries(
              Object.entries(backendQuarterMap).map(([yearKey, qSet]) => [
                yearKey,
                Array.from(qSet).sort((a, b) => a - b),
              ])
            )
          );
        } catch (e) {
          console.error("Failed to load existing governance entries", e);
        }
      };

      fetchAllEntries();
    }
  }, [addData]);

  // Fetch period options upfront so copy section can be shown based on available data.
  useEffect(() => {
    if (addData) {
      get("/governance/getGovernance?type=Completed").then((res) => {
        const quarterToId = (value) => {
          const raw = String(value ?? "").trim().toLowerCase();
          const asNum = Number(raw);
          if (asNum >= 1 && asNum <= 4) return asNum;
          if (raw === "q1" || raw.includes("jan-mar")) return 1;
          if (raw === "q2" || raw.includes("apr-jun")) return 2;
          if (raw === "q3" || raw.includes("jul-sep")) return 3;
          if (raw === "q4" || raw.includes("oct-dec")) return 4;
          return null;
        };

        const periods = (res.data.data || [])
          .map((entry) => {
            const ref = entry?._governanceDataRef || {};
            const moduleType = String(
              ref?.module ||
                entry?.entryType ||
                entry?.module ||
                (ref?.quarterToSubmit || entry?.quarterToSubmit || entry?.quarter
                  ? "quarterly"
                  : "annual")
            ).toLowerCase();
            const quarterVal =
              ref?.quarterToSubmit ?? entry?.quarterToSubmit ?? entry?.quarter;
            const yearVal = Number(
              ref?.yearToSubmit ?? entry?.yearToSubmit ?? entry?.year
            );
            const qId = quarterToId(quarterVal);
            return {
              id: entry?._id,
              title: entry?.title || `${quarterVal || ""}-${yearVal || ""}`,
              year: yearVal,
              quarterId: qId,
              module: moduleType,
            };
          })
          .filter((p) => p.id && p.year);
        setPeriodOptions(periods);
      });
    } else {
      setCopyYear("");
      setCopyQuarter("");
      setSelectedPeriod("");
      setPeriodOptions([]);
    }
  }, [addData]);

  const copyYearOptions = useMemo(() => {
    const targetYear = Number(formData?.year);
    const targetQuarter = Number(formData?.quarter);
    const set = new Set(
      periodOptions
        .filter((p) => {
          if (p.module !== entryType) return false;
          // Prevent copying from same selected target period
          if (entryType === "annual") {
            return Number(p.year) !== targetYear;
          }
          return !(
            Number(p.year) === targetYear &&
            Number(p.quarterId) === targetQuarter
          );
        })
        .map((p) => p.year)
        .filter(Boolean)
    );
    return Array.from(set).sort((a, b) => b - a);
  }, [periodOptions, entryType, formData?.year, formData?.quarter]);

  const copyQuarterOptions = useMemo(() => {
    if (entryType !== "quarterly") return [];
    const targetYear = Number(formData?.year);
    const targetQuarter = Number(formData?.quarter);
    const quarterSet = new Set(
      periodOptions
        .filter(
          (p) =>
            p.module === "quarterly" &&
            !(Number(p.year) === targetYear && Number(p.quarterId) === targetQuarter) &&
            Number(p.quarterId) >= 1 &&
            Number(p.quarterId) <= 4
        )
        .map((p) => Number(p.quarterId))
    );
    return quarterlist.filter((q) => quarterSet.has(Number(q.id)));
  }, [periodOptions, entryType, formData?.year, formData?.quarter]);

  const hasRequiredFields =
    Boolean(formData?.year) &&
    (entryType !== "quarterly" || Boolean(formData?.quarter));

  const isPrimarySelectionValid = useMemo(() => {
    if (!hasRequiredFields) return false;
    if (entryType === "annual") {
      if (existingAnnualYears.has(String(formData.year))) return false;
      return true;
    }
    const comboKey = `${formData.quarter}-${formData.year}`;
    return !existingQuarterlyCombos.has(comboKey);
  }, [
    hasRequiredFields,
    entryType,
    formData.year,
    formData.quarter,
    existingAnnualYears,
    existingQuarterlyYears,
    existingQuarterlyCombos,
  ]);

  const availablePeriodsForType = useMemo(
    () => periodOptions.filter((p) => p.module === entryType),
    [periodOptions, entryType]
  );

  useEffect(() => {
    if (copyPrevious !== "yes") return;
    if (!copyYear) {
      setSelectedPeriod("");
      return;
    }
    if (entryType === "annual") {
      const match = periodOptions.find(
        (p) => p.module === "annual" && Number(p.year) === Number(copyYear)
      );
      setSelectedPeriod(match?.id || "");
      return;
    }
    if (!copyQuarter) {
      setSelectedPeriod("");
      return;
    }
    const match = periodOptions.find(
      (p) =>
        p.module === "quarterly" &&
        Number(p.year) === Number(copyYear) &&
        Number(p.quarterId) === Number(copyQuarter)
    );
    setSelectedPeriod(match?.id || "");
  }, [copyPrevious, copyYear, copyQuarter, entryType, periodOptions]);

  useEffect(() => {
    if (copyPrevious !== "yes") return;
    if (entryType === "annual") {
      if (copyYear && !copyYearOptions.includes(Number(copyYear))) {
        setCopyYear("");
        setSelectedPeriod("");
      }
      return;
    }
    if (copyQuarter && !copyQuarterOptions.some((q) => Number(q.id) === Number(copyQuarter))) {
      setCopyQuarter("");
      setSelectedPeriod("");
    }
    if (copyYear && !copyYearOptions.includes(Number(copyYear))) {
      setCopyYear("");
      setSelectedPeriod("");
    }
  }, [copyPrevious, entryType, copyYear, copyQuarter, copyYearOptions, copyQuarterOptions]);

  useEffect(() => {
    const shouldShow = hasRequiredFields && isPrimarySelectionValid;
    setShowFormFields(shouldShow);
    if (!shouldShow) {
      setCopyPrevious("no");
      setCopyYear("");
      setCopyQuarter("");
      setSelectedPeriod("");
    }
  }, [hasRequiredFields, isPrimarySelectionValid]);

  const handlePeriodSelect = (periodId) => {
    setSelectedPeriod(periodId);
    const period = periodOptions.find((p) => p.id === periodId);
    if (period) {
      get(`/governance/getGovernance?id=${period.id}`).then((res) => {
        const data = res.data.data[0];
        setFormData((prev) => ({
          ...data,
          quarter: prev.quarter,
          year: prev.year,
        }));
      });
    }
  };

  const validation = () => {
    let newErrors = {}; // Initialize an empty error object
    let hasErrors = false;

    const isEmpty = (value) => value === "" || value === null || value === undefined;

    // ✅ Conditional validation based on entryType (annual vs quarterly)
    if (entryType === "annual") {
      // For annual, only validate year, remove quarter validation
      if (newErrors.quarter) {
        delete newErrors.quarter;
      }
      if (isEmpty(formData.year)) {
        newErrors.year = textError;
        hasErrors = true;
      } else if (existingAnnualYears.has(String(formData.year))) {
        newErrors.year = "An annual entry already exists for this year.";
        hasErrors = true;
      }
    } else if (entryType === "quarterly") {
      // For quarterly, validate both quarter and year
      if (isEmpty(formData.quarter)) {
        newErrors.quarter = textError;
        hasErrors = true;
      }
      if (isEmpty(formData.year)) {
        newErrors.year = textError;
        hasErrors = true;
      } else {
        const comboKey = `${formData.quarter}-${formData.year}`;
        if (existingQuarterlyCombos.has(comboKey)) {
          newErrors.quarter = "An entry already exists for this quarter and year.";
          hasErrors = true;
        }
      }
    }

    console.log("✅ Final Errors with Correct Indexes:", newErrors);
    setErrors(newErrors); // ✅ Directly sets nested errors in state
    return !hasErrors; // Return true if no errors
  };

  const validateForm = () => {
    return validation();
  };

  const handleCopyAndCreate = async () => {
    // 1. Fetch the data for the selected period
    const res = await get(`/governance/getGovernance?id=${selectedPeriod}`);
    const oldData = res.data.data;

    // Map selected quarter id to label (e.g., "Jan-Mar") and numeric value
    let quarterLabel = null;
    let quarterToSubmit = null;
    if (entryType === "quarterly" && formData.quarter) {
      const selectedQuarter = quarterlist.find(
        (q) => String(q.id) === String(formData.quarter)
      );
      quarterLabel = selectedQuarter?.month || null;
      quarterToSubmit = selectedQuarter ? selectedQuarter.id : null;
    }

    // 2. Prepare new data for the new entry
    const newEntry = {
      ...oldData, // copy all fields from selected period
      year: formData.year,
      // For quarterly, send human-readable range and numeric quarter separately
      quarter: entryType === "quarterly" ? quarterLabel : null,
      quarterToSubmit: entryType === "quarterly" ? quarterToSubmit : null,
      entryType, // set module type based on current popup selection
      // Let backend recompute title/module text for this new period
    };
    delete newEntry._id;
    delete newEntry.createdAt;
    delete newEntry.updatedAt;
    delete newEntry.title;

    // 3. Create the new entry
    await post("/governance/create", newEntry);
  };

  const Save = async () => {
    if (!validateForm()) return;
    try {
      // Map selected quarter id to label (e.g., "Jan-Mar") and numeric value
      let quarterLabel = null;
      let quarterToSubmit = null;
      if (entryType === "quarterly" && formData.quarter) {
        const selectedQuarter = quarterlist.find(
          (q) => String(q.id) === String(formData.quarter)
        );
        quarterLabel = selectedQuarter?.month || null;
        quarterToSubmit = selectedQuarter ? selectedQuarter.id : null;
      }

      let dataToSend = {
        year: formData.year,
        // For quarterly, send human-readable range and numeric quarter separately
        quarter: entryType === "quarterly" ? quarterLabel : null,
        quarterToSubmit: entryType === "quarterly" ? quarterToSubmit : null,
        module: entryType, // Include entry type in the data
      };
      if (copyPrevious === "yes" && selectedPeriod) {
        dataToSend.copyPrevious = true;
        dataToSend.sourceId = selectedPeriod;
      } else {
        dataToSend.copyPrevious = false;
      }
      const response = await post("/governance/create", dataToSend);
      const message = response?.data?.message;
      setsnackOpen({
        open: true,
        message:
          message || "Report generation is in progress, Check after 10 mins.",
        severity: "success",
      });
      setFormData(initialState);
      handleClose();
    } catch (error) {
      if (error?.response?.data?.message) {
        const errorMessages = error.response.data.message;
        if (errorMessages.includes("already exists")) {
          setErrors({
            ...errors,
            year: errorMessages,
          });
        } else {
          setsnackOpen({
            open: true,
            message: errorMessages,
            severity: "error",
          });
        }
      } else {
        setsnackOpen({
          open: true,
          message: error.message || "An error occurred while saving the data.",
          severity: "error",
        });
      }
    }
  };

  const handleExitAgree = () => {
    setErrors({});
    setEditDialog(false);
    setFormData(initialState);
    setFormData2(initialState);
    setCopyPrevious("no");
    setSelectedPeriod("");
    setPeriodOptions([]);
    setEntryType("quarterly"); // Reset to default
    setShowFormFields(false); // Reset form fields visibility
    handleClose();
  };

  const handleExit = () => {
    const isFormDataUnchanged = Object.keys(formData).every(
      (key) => formData[key] === formData2[key]
    );
    if (isFormDataUnchanged) {
      handleExitAgree();
    } else {
      setEditDialog(true);
    }
  };

  const handleMonthChange = (event) => {
    setErrors && setErrors({ ...errors, quarter: "", year: "" });
    const selectedMonthIndex = event.target.value;
    setFormData({
      ...formData,
      quarter: selectedMonthIndex,
    });
  };

  const handleEntryTypeChange = (event) => {
    const newEntryType = event.target.value;
    setEntryType(newEntryType);
    // Reset form data and errors when switching entry type
    setErrors({});
    // Clear both quarter and year when switching modes so selections
    // from annual do not leak into quarterly and vice versa.
    setFormData({
      ...formData,
      quarter: "",
      year: "",
    });
    setCopyYear("");
    setCopyQuarter("");
    setSelectedPeriod("");
  };

  const handleYearChange = (event) => {
    setErrors && setErrors({ ...errors, year: "" });
    setFormData({
      ...formData,
      year: event.target.value,
    });
  };

  const list = () => (
    <Box sx={{ p: 3, backgroundColor: "#fff", minWidth: "400px" }}>
        <Typography
          variant="h6"
          sx={{ fontWeight: "bold", mb: 2 }}
        >
          Select Entry Type
        </Typography>
        <Typography
          variant="body2"
          sx={{ mb: 3, color: "#666" }}
        >
          Choose how you would like to enter the data. You can change this later if needed.
        </Typography>
        {/* Entry Type Selection */}
        <Box sx={{ mb: 3 }}>
          <FormControl component="fieldset" sx={{ width: "100%" }}>
            <RadioGroup
              value={entryType}
              onChange={handleEntryTypeChange}
              sx={{ flexDirection: "row", gap: 2 }}
            >
              <FormControlLabel
                value="annual"
                control={
                  <Radio
                    sx={{
                      color: "#1c486b",
                      "&.Mui-checked": {
                        color: "#1c486b",
                      },
                    }}
                  />
                }
                label={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography sx={{ fontSize: "0.95rem" }}>
                      Annual Module
                    </Typography>
                  </Box>
                }
                sx={{
                
                  "& .MuiFormControlLabel-label": {
                    marginLeft: "8px",
                  },
                }}
              />
              <FormControlLabel
                value="quarterly"
                control={
                  <Radio
                    sx={{
                      color: "#1c486b",
                      "&.Mui-checked": {
                        color: "#1c486b",
                      },
                    }}
                  />
                }
                label={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    
                    <Typography sx={{ fontSize: "0.95rem" }}>
                      Quarterly Module
                    </Typography>
                  </Box>
                }
                sx={{
                  "& .MuiFormControlLabel-label": {
                    marginLeft: "8px",
                  },
                }}
              />
            </RadioGroup>
          </FormControl>
        </Box>

        {/* Month/Quarter & Year Inputs - Inline layout */}
        {/* <Box sx={{ mb: 2, display: "flex", gap: 2 }}>
          {/* Month/Quarter Input - Show for quarterly */}
          {entryType === "quarterly" && (
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: "bold", mb: "-0.5rem" }}
              >
                Select Quarter
              </Typography>
              <SelectField
                name={"Select Month"}
                value={formData?.quarter}
                handleChange={handleMonthChange}
                label="Select Quarter"
                field="quarter"
                errors={errors?.["quarter"] || ""}
                defaultValue={"Tap to select"}
                options={filteredQuarterOptions.map((q) => ({ ...q, month: `Q${q.id}` }))}
              />
            </Box>
          )}

          {/* Year Input */}
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: "bold", mb: "-0.5rem" }}
            >
              Select Year
            </Typography>
            <SelectField
              name={"Year"}
              value={formData?.year}
              handleChange={handleYearChange}
              options={filteredYears}
              errors={errors?.["year"] || ""}
              defaultValue={"Tap to select"}
            />
          </Box>
        {/* </Box>  */}

        {/* Copy Previous Data Option - show when required fields are filled and previous data exists */}
        {showFormFields && hasPreviousEntries && availablePeriodsForType.length > 0 && (
          <Box sx={{ mt: 2, mb: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 1 }}>
              Do you want to copy the previous data?
            </Typography>
            <FormControl component="fieldset">
              <RadioGroup
                row
                value={copyPrevious}
                onChange={(e) => {
                  setCopyPrevious(e.target.value);
                  setCopyYear("");
                  setCopyQuarter("");
                  setSelectedPeriod("");
                }}
              >
                <FormControlLabel value="yes" control={<Radio />} label="Yes" />
                <FormControlLabel value="no" control={<Radio />} label="No" />
              </RadioGroup>
            </FormControl>
            {copyPrevious === "yes" && (
              <Box sx={{ mt: 2, display: "flex", gap: 2 }}>
                {entryType === "quarterly" && (
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: "-0.5rem" }}>
                      Select Quarter
                    </Typography>
                    <SelectField
                      name="copyQuarter"
                      value={copyQuarter}
                      handleChange={(e) => setCopyQuarter(e.target.value)}
                      options={copyQuarterOptions.map((q) => ({ ...q, month: `Q${q.id}` }))}
                      label="Select Quarter"
                      defaultValue="Tap to select"
                    />
                  </Box>
                )}
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: "-0.5rem" }}>
                    Select Year
                  </Typography>
                  <SelectField
                    name="copyYear"
                    value={copyYear}
                    handleChange={(e) => {
                      setCopyYear(e.target.value);
                    }}
                    options={copyYearOptions}
                    label="Select Year"
                    defaultValue="Tap to select"
                  />
                </Box>
              </Box>
            )}
          </Box>
        )}

        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 3 }}>
          <Button
            variant="text"
            sx={{
              color: "#000",
              textTransform: "none",
            }}
            onClick={handleExit}
          >
            Back
          </Button>
          <Button
            variant="contained"
            sx={{
              backgroundColor: "#0a3f73",
              textTransform: "none",
              "&:disabled": {
                backgroundColor: "#e0e0e0",
                color: "#999",
              },
            }}
            onClick={Save}
            disabled={
              !hasRequiredFields ||
              (copyPrevious === "yes" &&
                ((entryType === "quarterly" &&
                  (!copyYear || !copyQuarter || !selectedPeriod)) ||
                  (entryType === "annual" && (!copyYear || !selectedPeriod))))
            }
          >
            Proceed
          </Button>
        </Box>
    </Box>
  );

  return (
    <>
      {editDialog && (
        <EditDialog {...{ editDialog, setEditDialog, handleExitAgree }} />
      )}
      <Dialog
        open={addData}
        onClose={handleExit}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "8px",
            boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.15)",
          },
        }}
      >
        <DialogContent sx={{ p: 0, "&.MuiDialogContent-root": { padding: 0 } }}>
          {list()}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AddGovernance;
